import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { ScreenBackdrop } from './ScreenBackdrop';
import { ChevronRight, CopyIcon } from './icons';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useHaptic } from '../../hooks/useHaptic';
import { useReferral } from '../../hooks/useReferral';
import type { ReferralRewardMode } from '../../lib/referral';

/** Share an invite link, pick payout mode, and see referral earnings. */
export function ReferralScreen({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const { impact, notify } = useHaptic();
  const {
    code,
    link,
    invitedCount,
    invited,
    rewardMode,
    enabled: rewardsEnabled,
    rewardPct,
    vaults,
    earnedUsd,
    earnedIncpt,
    earnedLiquidUsd,
    recentRewards,
    setRewardMode,
    settingMode,
    loading,
    error,
    refetch,
  } = useReferral();
  useBackButton(onBack);
  const tgBack = hasTelegramBackButton();
  const [copied, setCopied] = useState(false);
  const [modeError, setModeError] = useState(false);
  const [pendingMode, setPendingMode] = useState<ReferralRewardMode | null>(null);

  const vaultLabel = (kind: string) => {
    if (kind === 'liquid') return t('referral.vaultLiquid');
    if (kind === 'monthly') return t('referral.vaultMonthly');
    if (kind === 'maximum') return t('referral.vaultMaximum');
    return kind;
  };

  const vaultsLabel =
    vaults.length === 0
      ? t('referral.vaultsNone')
      : vaults.map(vaultLabel).join(i18n.language.startsWith('ru') || i18n.language.startsWith('uk') ? ' и ' : ' & ');

  const copyVars = { pct: rewardPct ?? '—', vaults: vaultsLabel };

  const copy = async () => {
    if (!link) return;
    impact('light');
    try {
      await navigator.clipboard.writeText(link);
      notify('success');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      notify('error');
    }
  };

  const share = () => {
    if (!link) return;
    impact('medium');
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(
      t('referral.shareText'),
    )}`;
    window.Telegram?.WebApp?.openTelegramLink?.(url) ?? window.open(url, '_blank');
  };

  const pickMode = async (mode: ReferralRewardMode) => {
    if (mode === rewardMode || settingMode || rewardMode == null) return;
    impact('light');
    setModeError(false);
    setPendingMode(mode);
    try {
      await setRewardMode(mode);
      notify('success');
    } catch {
      notify('error');
      setModeError(true);
    } finally {
      setPendingMode(null);
    }
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  if (error) {
    return (
      <div className="relative min-h-screen">
        <ScreenBackdrop height={520} fadeStart={20} />
        <div className="relative z-10 flex min-h-screen flex-col px-4 pb-10 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
          <header className="flex items-center gap-3 py-2">
            {!tgBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
                aria-label={t('common.back')}
              >
                <ChevronRight size={16} className="rotate-180 text-ink" />
              </button>
            )}
            <h1 className="text-[20px] font-medium text-ink">{t('referral.title')}</h1>
          </header>
          <div className="mt-8 rounded-[20px] bg-white p-5 text-center shadow-card">
            <p className="text-[14px] text-muted">{t('referral.loadError')}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-4 h-11 w-full rounded-[14px] bg-ink text-[15px] font-semibold text-white"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ScreenBackdrop height={520} fadeStart={20} />

      <div className="relative z-10 flex min-h-screen flex-col px-4 pb-10 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label={t('common.back')}
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">{t('referral.title')}</h1>
        </header>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted">
          {loading
            ? '…'
            : rewardsEnabled
              ? t('referral.subtitle', copyVars)
              : t('referral.subtitlePaused')}
        </p>

        {/* Invite count */}
        <div className="mt-6 rounded-[24px] bg-white p-5 text-center shadow-card">
          <div className="text-[13px] text-muted">{t('referral.invited')}</div>
          <div className="mt-1 text-[40px] font-semibold leading-none tracking-[-1px] text-ink">
            {loading ? '—' : invitedCount}
          </div>
        </div>

        {/* Earnings — always visible so zero earners understand the product */}
        <div className="mt-4 rounded-[20px] bg-white p-4 shadow-card">
          <div className="text-[13px] text-muted">{t('referral.earned')}</div>
          <div className="mt-1 text-[22px] font-semibold tracking-[-0.5px] text-ink">
            {loading ? '—' : `$${earnedUsd}`}
          </div>
          {!loading && Number(earnedUsd) <= 0 ? (
            <p className="mt-2 text-[12.5px] leading-[1.45] text-muted">
              {t('referral.earnedEmpty', copyVars)}
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-muted">
              {Number(earnedLiquidUsd) > 0 && (
                <span>{t('referral.earnedLiquid', { amount: earnedLiquidUsd })}</span>
              )}
              {Number(earnedIncpt) > 0 && (
                <span>{t('referral.earnedIncpt', { amount: earnedIncpt })}</span>
              )}
            </div>
          )}
        </div>

        {/* Payout preference — hidden while rewards are admin-paused */}
        {rewardsEnabled && (
          <div className="mt-4 rounded-[20px] bg-white p-4 shadow-card">
            <div className="text-[13px] font-medium text-ink">{t('referral.payoutTitle')}</div>
            <p className="mt-1 text-[12.5px] leading-[1.45] text-muted">{t('referral.payoutHint')}</p>
            <div className="mt-3 flex gap-2" role="radiogroup" aria-label={t('referral.payoutTitle')}>
              {(
                [
                  { mode: 'liquid' as const, label: t('referral.payoutLiquid') },
                  { mode: 'incpt' as const, label: t('referral.payoutIncpt') },
                ] as const
              ).map(({ mode, label }) => {
                const active = rewardMode === mode;
                const pending = pendingMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={settingMode || loading || rewardMode == null}
                    onClick={() => void pickMode(mode)}
                    className={`h-11 flex-1 rounded-[14px] text-[14px] font-medium transition-colors disabled:opacity-50 ${
                      active || pending
                        ? 'bg-ink text-white'
                        : 'bg-black/[0.05] text-ink active:bg-black/[0.1]'
                    }`}
                  >
                    {pending ? t('referral.saving') : label}
                  </button>
                );
              })}
            </div>
            {modeError && (
              <p className="mt-2 text-[12.5px] text-rose-600">{t('referral.modeError')}</p>
            )}
          </div>
        )}

        {/* Link + QR */}
        <div className="mt-4 rounded-[20px] bg-white p-4 shadow-card">
          <div className="flex justify-center py-3">
            <QRCodeSVG
              value={link || ' '}
              size={180}
              bgColor="#ffffff"
              fgColor="#121212"
              level="M"
              marginSize={0}
            />
          </div>
          <div className="text-[12px] text-muted">{t('referral.yourLink')}</div>
          <div className="mt-1.5 break-all font-mono text-[12.5px] leading-[1.45] text-ink">
            {link ?? '…'}
          </div>
          {code && (
            <div className="mt-2 text-[12px] text-muted">
              {t('referral.yourCode')} <span className="font-mono text-ink">{code}</span>
            </div>
          )}
        </div>

        <div className="mt-3 flex gap-2.5">
          <button
            type="button"
            onClick={() => void copy()}
            disabled={!link}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[15px] bg-black/[0.05] text-[15px] font-medium text-ink active:bg-black/[0.1] disabled:opacity-40"
          >
            <CopyIcon size={18} />
            {copied ? t('common.copied') : t('common.copy')}
          </button>
          <button
            type="button"
            onClick={share}
            disabled={!link}
            className="h-12 flex-1 rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-40"
          >
            {t('referral.share')}
          </button>
        </div>

        {/* Recent rewards */}
        {!loading && recentRewards.length > 0 && (
          <div className="mt-7">
            <h2 className="text-[15px] font-medium text-ink">{t('referral.recent')}</h2>
            <div className="mt-3 flex flex-col gap-2">
              {recentRewards.map((r, i) => (
                <div
                  key={`${r.createdAt}-${i}`}
                  className="flex items-center justify-between rounded-[16px] bg-white px-4 py-3 shadow-card"
                >
                  <div>
                    <div className="text-[14px] text-ink">
                      {r.paidAs === 'incpt'
                        ? t('referral.earnedIncpt', { amount: r.paidAmount })
                        : t('referral.earnedLiquid', { amount: r.paidAmount })}
                    </div>
                    <div className="mt-0.5 text-[12px] text-muted">
                      {vaultLabel(r.vaultKind)} · ${r.rewardUsd}
                    </div>
                  </div>
                  <span className="text-[12.5px] text-muted">{fmtDate(r.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Who joined */}
        <div className="mt-7">
          <h2 className="text-[15px] font-medium text-ink">{t('referral.joined')}</h2>
          {loading ? (
            <div className="mt-3 flex flex-col gap-2">
              {[0, 1].map((i) => (
                <div key={i} className="rounded-[16px] bg-white p-3.5 shadow-card">
                  <div className="skeleton h-3.5 w-28 rounded-full" />
                </div>
              ))}
            </div>
          ) : invited.length === 0 ? (
            <div className="mt-3 rounded-[20px] bg-white p-5 text-center shadow-card">
              <p className="text-[13px] leading-[1.5] text-muted">{t('referral.empty')}</p>
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              {invited.map((u, i) => (
                <div
                  key={`${u.name}-${i}`}
                  className="flex items-center justify-between rounded-[16px] bg-white px-4 py-3 shadow-card"
                >
                  <span className="text-[14px] text-ink">{u.name}</span>
                  <span className="text-[12.5px] text-muted">{fmtDate(u.joinedAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onBack}
          className="mt-7 h-12 w-full rounded-[15px] bg-black/[0.05] text-[15px] font-medium text-ink active:bg-black/[0.1]"
        >
          {t('common.back')}
        </button>
      </div>
    </div>
  );
}
